import React from 'react';

export default function AnalysisPanel({ hitFrequencies, cardSelections, bets, denomination, selectedSpotSize, paytable, activePaytable }) {
    // Removed unused combination function

    // Removed unused calculateEV function
  const formatCurrency = (value) => {
    return `$${value.toFixed(2)}`;
  };

  const getRowClass = (returnPct) => {
    if (returnPct >= 100) return 'row-return-good';
    if (returnPct >= 80) return 'row-return-fair';
    return 'row-return-poor';
  };

  const getReturnClass = (returnPct, hasData) => {
    if (!hasData) return '';
    if (returnPct >= 100) return 'return-good';
    if (returnPct >= 80) return 'return-fair';
    return 'return-poor';
  };

  // Get cards that have been played (have bets)
  const playedCards = Object.keys(bets)
    .filter(card => bets[card] > 0)
    .sort();

  // If a spot size is selected, filter for those cards
  const filteredCards = selectedSpotSize != null
    ? playedCards.filter(card => cardSelections[card].length === Number(selectedSpotSize))
    : playedCards;

  // Find the maximum spots across all played cards
  const maxSpots = playedCards.length > 0
    ? Math.max(...playedCards.map(card => cardSelections[card].length))
    : 0;

  // Generate hit columns based on max spots
  const hitColumns = Array.from({ length: maxSpots + 1 }, (_, i) => i);
  const totalColumns = 2 + hitColumns.length + 3; // Card, Spots, hits, Bet, Won, Return

  return (
    <aside className="analysis-panel">
      <div className="analysis-header">
        <h3>Card Analysis{selectedSpotSize ? ` — ${selectedSpotSize}-Spot` : ''}</h3>
      </div>
      <div className="analysis-table-container">
        <table className="analysis-table">
          <thead>
            <tr>
              <th>Card</th>
              <th>Spots</th>
              {hitColumns.map(hit => (
                <th
                  key={hit}
                  style={selectedSpotSize && Number(selectedSpotSize) === hit ? { background: '#ffd600', color: '#222', fontWeight: 900 } : {}}
                >
                  {hit}
                </th>
              ))}
              <th>Bet</th>
              <th>Won</th>
              <th>Return</th>
            </tr>
          </thead>
          <tbody>
            {(selectedSpotSize ? filteredCards.length === 0 : playedCards.length === 0) ? (
              <tr>
                <td colSpan={totalColumns} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  No cards played yet. Place bets and run games to see analysis.
                </td>
              </tr>
            ) : (
              <>
                {(selectedSpotSize ? filteredCards : playedCards).map(card => {
                  const spots = cardSelections[card].length;
                  const freq = hitFrequencies[card] || {};
                  const totalGames = Object.keys(freq)
                    .filter(key => key !== 'totalWon')
                    .reduce((sum, key) => sum + (freq[key] || 0), 0);
                  const totalWon = freq.totalWon || 0;
                  const totalWagered = totalGames * bets[card] * denomination;
                  const returnPct = totalWagered > 0 ? (totalWon / totalWagered * 100) : 0;

                  return (
                    <tr key={card} className={getRowClass(returnPct)}>
                      <td className="analysis-cell-bold">{card}</td>
                      <td>{spots}</td>
                      {hitColumns.map(hit => {
                        const isActiveCol = selectedSpotSize && Number(selectedSpotSize) === hit;
                        return (
                          <td key={hit} style={isActiveCol ? { background: '#ffd600', color: '#222', fontWeight: 700 } : {}}>
                            {freq[hit] > 0 ? freq[hit] : '-'}
                          </td>
                        );
                      })}
                      <td className="analysis-cell-bold">{formatCurrency(totalWagered)}</td>
                      <td className="analysis-cell-bold">{formatCurrency(totalWon)}</td>
                      <td className="analysis-cell-bold">{returnPct.toFixed(2)}%</td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr style={{ backgroundColor: '#e0e0e0', fontWeight: selectedSpotSize ? 900 : 'bold' }}>
                  <td colSpan={2}>Totals</td>
                  {hitColumns.map((hit, idx) => {
                    // Sum hits for selected spot size if selected, else blank
                    if (!selectedSpotSize) return <td key={idx}></td>;
                    // Sum for all cards of selected spot size
                    const sum = filteredCards.reduce((acc, card) => {
                      const freq = hitFrequencies[card] || {};
                      return acc + (freq[hit] || 0);
                    }, 0);
                    return <td key={idx}>{sum > 0 ? sum : ''}</td>;
                  })}
                  <td>
                    {formatCurrency(
                      (selectedSpotSize ? filteredCards : playedCards).reduce((sum, card) => {
                        const freq = hitFrequencies[card] || {};
                        const totalGames = Object.keys(freq)
                          .filter(key => key !== 'totalWon')
                          .reduce((s, key) => s + (freq[key] || 0), 0);
                        return sum + totalGames * bets[card] * denomination;
                      }, 0)
                    )}
                  </td>
                  <td>
                    {formatCurrency(
                      (selectedSpotSize ? filteredCards : playedCards).reduce((sum, card) => {
                        const freq = hitFrequencies[card] || {};
                        return sum + (freq.totalWon || 0);
                      }, 0)
                    )}
                  </td>
                  {(() => {
                      const cards = selectedSpotSize ? filteredCards : playedCards;
                      const totalWagered = cards.reduce((sum, card) => {
                        const freq = hitFrequencies[card] || {};
                        const totalGames = Object.keys(freq)
                          .filter(key => key !== 'totalWon')
                          .reduce((s, key) => s + (freq[key] || 0), 0);
                        return sum + totalGames * bets[card] * denomination;
                      }, 0);
                      const totalWon = cards.reduce((sum, card) => {
                        const freq = hitFrequencies[card] || {};
                        return sum + (freq.totalWon || 0);
                      }, 0);
                      const returnPct = totalWagered > 0 ? (totalWon / totalWagered) * 100 : 0;
                      return (
                        <td className={getReturnClass(returnPct, totalWagered > 0)}>
                          {totalWagered > 0 ? returnPct.toFixed(2) + '%' : '0.00%'}
                        </td>
                      );
                  })()}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </aside>
  );
}
